// Role-based menu and route permissions for SOM-TURK PLC Finance System.
//
// Phase 19 PR-2 hot-fix (2026-07-06): the nav array is now permission-key
// driven. Every nav item declares a `permKey` (or `permKeys` array — ALL
// required); the sidebar filter checks `useAuth().can(permKey)` first and
// only falls back to the legacy `allowed` role list when no key is set.
// `system_admin` always sees everything.

export const ROLES = {
  SYSTEM_ADMIN: "system_admin",
  SOMTURK_DATA_ENTRY: "somturk_data_entry",
  SOMTURK_OBSERVER: "somturk_observer",
  SALIHI_ACCOUNTANT: "salihi_accountant",
  SALIHI_SENIOR_ACCOUNTANT: "salihi_senior_accountant",
  SALIHI_FINANCE_MANAGER: "salihi_finance_manager",
  SALIHI_COMPLIANCE_OFFICER: "salihi_compliance_officer",
  INTERNAL_AUDITOR: "internal_auditor",
};

export const ROLE_LABELS = {
  system_admin: "System Administrator",
  somturk_data_entry: "SOM-TURK Data Entry",
  somturk_observer: "SOM-TURK Observer",
  salihi_accountant: "Salihi Accountant",
  salihi_senior_accountant: "Salihi Senior Accountant",
  salihi_finance_manager: "Salihi Finance Manager",
  salihi_compliance_officer: "Salihi Compliance Officer",
  internal_auditor: "Internal Auditor",
};

export const NAV_SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "transactions", label: "Transactions" },
  { key: "invoices", label: "Invoices" },
  { key: "accounting", label: "Accounting" },
  { key: "taxes", label: "Taxes" },
  { key: "reports", label: "Reports" },
  { key: "operations", label: "Operations" },
  { key: "admin", label: "Admin" },
];

export const SOMTURK_ROLES = new Set([
  ROLES.SOMTURK_DATA_ENTRY,
  ROLES.SOMTURK_OBSERVER,
]);

export function homePathFor(user) {
  if (!user) return "/login";
  // Phase 30 (2026-07-17) — unified dashboard. All roles land on
  // /dashboard; role-aware tile filtering happens inside the component.
  return "/dashboard";
}

// Every nav item carries `permKeys`: ALL must be `can()`=true for the item
// to be visible. Section keys (e.g. `accounting.section.access`) gate the
// whole group; the page-specific `*.view` key gates the individual page.
export const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", path: "/dashboard",
    permKeys: ["overview.dashboard.view"], section: "overview" },
  // Phase 30 — Observer Dashboard nav item removed (unified into main
  // Dashboard). Left as a no-op path for now; the route redirects.
  { key: "company", label: "Company Profile", path: "/company",
    permKeys: ["overview.company_profile.view"], section: "overview" },

  { key: "transactions", label: "Transaction Submission", path: "/transactions",
    permKeys: ["transactions.submission.view"], section: "transactions" },
  { key: "documents", label: "Documents", path: "/documents",
    permKeys: ["transactions.documents.view"], section: "transactions" },

  { key: "invoices", label: "Connected Invoices", path: "/invoices",
    permKeys: ["invoices.section.access", "invoices.connected.view"], section: "invoices" },
  // Phase 3 (2026-08-09) — Manual Invoices removed from the sidebar per
  // user request; the /manual-invoices routes now redirect to /dashboard
  // in App.js. Component files kept in the tree for reversibility.

  { key: "accounting", label: "Accounting Records", path: "/accounting",
    permKeys: ["accounting.section.access", "accounting.records.view"], section: "accounting" },
  { key: "bookkeeping", label: "Bookkeeping", path: "/bookkeeping",
    permKeys: ["accounting.section.access", "accounting.bookkeeping.view"], section: "accounting" },
  { key: "payables", label: "Accounts Payable", path: "/payables",
    permKeys: ["accounting.section.access", "accounting.ap.view"], section: "accounting" },
  { key: "receivables", label: "Accounts Receivable", path: "/receivables",
    permKeys: ["accounting.section.access", "accounting.ar.view"], section: "accounting" },
  { key: "bank", label: "Bank and Cash", path: "/bank",
    permKeys: ["accounting.section.access", "accounting.bank.view"], section: "accounting" },
  { key: "petty", label: "Petty Cash", path: "/petty-cash",
    permKeys: ["accounting.section.access", "accounting.petty_cash.view"], section: "accounting" },
  { key: "payroll", label: "Payroll Accounting", path: "/payroll",
    permKeys: ["accounting.section.access", "accounting.payroll.view"], section: "accounting" },
  { key: "loans", label: "Loans & Borrowings", path: "/loans",
    permKeys: ["accounting.section.access", "accounting.loans.view"], section: "accounting" },

  { key: "taxes", label: "Taxes", path: "/taxes",
    permKeys: ["taxes.section.access", "taxes.view"], section: "taxes" },
  { key: "tax_settings", label: "Tax Settings", path: "/settings/taxes",
    permKeys: ["taxes.section.access", "taxes.settings.view"], section: "taxes" },

  { key: "reports", label: "Financial Reports", path: "/reports",
    permKeys: ["reports.section.access", "reports.view"], section: "reports" },
  // Phase 3 (2026-08-09) — Finance Integrity removed from sidebar; route
  // redirects to /dashboard.

  { key: "compliance", label: "Compliance Procedures", path: "/compliance",
    permKeys: ["operations.section.access", "operations.compliance.view"], section: "operations" },
  // Phase 3 (2026-08-09) — Internal Controls removed from sidebar; route
  // redirects to /dashboard.
  { key: "suppliers", label: "Suppliers", path: "/suppliers",
    permKeys: ["operations.section.access", "operations.suppliers.view"], section: "operations" },
  { key: "customers", label: "Customers", path: "/customers",
    permKeys: ["operations.section.access", "operations.customers.view"], section: "operations" },
  { key: "employees", label: "Employees", path: "/employees",
    permKeys: ["operations.section.access", "operations.employees.view"], section: "operations" },
  // Phase 3 (2026-08-09) — Tasks and Follow Up removed from sidebar;
  // route redirects to /dashboard.
  { key: "approvals", label: "Approvals", path: "/approvals",
    permKeys: ["operations.section.access", "operations.approvals.view"], section: "operations" },

  { key: "audit", label: "Audit Logs", path: "/audit-logs",
    permKeys: ["admin.section.access", "admin.audit_logs.view"], section: "admin" },
  { key: "settings", label: "Settings", path: "/settings",
    permKeys: ["admin.section.access", "admin.settings.view"], section: "admin" },
];

export const ADMIN_ONLY_NAV = [
  { key: "users", label: "Users", path: "/users",
    permKeys: ["admin.section.access", "admin.users.view"], section: "admin" },
  { key: "role-permissions", label: "Role Permissions",
    path: "/settings/role-permissions",
    permKeys: ["admin.section.access", "admin.users.manage_permissions"], section: "admin" },
  // Phase A (2026-08-20) — Security Center. Visible to system_admin (RoleGuard).
  { key: "security", label: "Security Center", path: "/security",
    permKeys: ["admin.section.access"], section: "admin" },
];

// `can` is `useAuth().can` — already returns true for system_admin.
export function canAccess(item, can) {
  if (!can) return false;
  const keys = item.permKeys || (item.permKey ? [item.permKey] : null);
  if (!keys || !keys.length) return true; // no gate → visible to all logged-in
  return keys.every((k) => can(k));
}

// Single source of truth used by the sidebar AND by route guards.
// Pass `useAuth().can`.
export function visibleNav(can) {
  return [...NAV_ITEMS, ...ADMIN_ONLY_NAV].filter((it) => canAccess(it, can));
}
